import React from 'react';
import { Button } from './ui/button';
import { WATCH_SESSION_COPY } from '../constants/watchSession';
import { formatStartTime } from '../utils/formatStartTime';
import type { WatchableSession } from '../types/liveSession';

interface LiveSessionRowProps {
  session: WatchableSession;
  /** The user's own bot: Resume it as its owner. */
  onResume: (session: WatchableSession) => void;
  /** Anyone else's call, bot or desktop: open the choice step (Join as Co-Seller / Join as Observer). */
  onJoin: (session: WatchableSession) => void;
}

/**
 * One live call in "Watch a Session": its name, then whose it is, the mode
 * (one neutral chip: categories carry no hue) and when it started. The user's
 * own bot offers Resume (unchanged); every other call offers exactly one
 * action, Join, which opens the choice step (owner, 2026-10-07, the same rows
 * as the desktop apps).
 */
const LiveSessionRow: React.FC<LiveSessionRowProps> = ({ session, onResume, onJoin }) => {
  const ownerLabel = session.isOwn ? WATCH_SESSION_COPY.you : session.ownerName || WATCH_SESSION_COPY.unknownOwner;
  const modeLabel = session.mode === 'bot' ? WATCH_SESSION_COPY.modeBot : WATCH_SESSION_COPY.modeStandalone;
  const startTime = formatStartTime(session.startedAt);
  const actionLabel = session.isOwn ? WATCH_SESSION_COPY.resume : WATCH_SESSION_COPY.join;
  const sessionLabel = session.sessionName || WATCH_SESSION_COPY.untitled;
  const handleAction = () => (session.isOwn ? onResume(session) : onJoin(session));

  return (
    <li className="flex items-center justify-between border-b border-hairline py-2.5">
      <div className="min-w-0 pr-3">
        <p className="text-[13px] font-semibold text-ink truncate">{sessionLabel}</p>
        <p className="text-[11px] text-ink-muted mt-0.5 flex flex-wrap items-center gap-1.5">
          <span>{ownerLabel}</span>
          <span className="px-1.5 py-0.5 rounded-full border border-hairline bg-surface-subtle text-ink-secondary">
            {modeLabel}
          </span>
          {startTime && <span>{startTime}</span>}
        </p>
        {session.isOwn && session.abandoned && (
          <p className="text-[11px] text-warning mt-0.5">{WATCH_SESSION_COPY.abandoned}</p>
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleAction}
        className="shrink-0 text-xs"
        aria-label={`${actionLabel} ${sessionLabel}`}
      >
        {actionLabel}
      </Button>
    </li>
  );
};

export default LiveSessionRow;
